import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Section, SectionHeader, Panel, prefersReducedMotion } from "@/components/system";

const testimonials = [
  { id: 1, quote: "Kyrosity AI has consistently reduced our customer acquisition cost by 30 to 60%. That kind of performance impact is rare, and they deliver it at a speed that no traditional production setup can match.", name: "Sakti Bevara",      role: "Head of Production",       company: "Pocket FM" },
  { id: 2, quote: "After experimenting with thousands of content companies, we finally found one that delivers with speed and actually generates results. Kyrosity operates at a completely different level.",              name: "Sarthak Bhardwaj",  role: "Co founder",               company: "Pokus.ai" },
  { id: 3, quote: "Kyrosity AI is hands down one of the best AI creative studios out there right now. The quality, the speed, the strategic thinking. The work speaks for itself.",                                          name: "Tarun Tiwari",      role: "Co founder",               company: "Zostel" },
  { id: 4, quote: "The speed at which Kyrosity operates without any compromise on quality is genuinely impressive. They produce at a scale that would take a traditional agency months, in days.",                           name: "Abhishek Madhavan", role: "ex Senior Vice President", company: "MPL" },
  { id: 5, quote: "The content quality coming out of Kyrosity is unlike anything else in the market. AI powered but never generic. Every piece feels built for our brand specifically.",                                     name: "Utsav Poddar",      role: "Head of User Acquisition", company: "Sugar.fit" },
  { id: 6, quote: "The content Kyrosity produces is the strongest we have seen. AI at the core, but with a creative sharpness that most agencies with full human teams still cannot match.",                                 name: "Raghav Gupta",      role: "Founder",                  company: "Pokus.ai" },
  { id: 7, quote: "Kyrosity brings creative sophistication that matches global brand standards, delivered faster and more cost efficiently than anything we had worked with before.",                                        name: "Kuldeep Jha",       role: "Regional Manager",         company: "Philip Morris International" },
  { id: 8, quote: "The creative output from Kyrosity is sharp, polished, and built for a global audience. What impressed me most was how consistently they delivered at scale without losing quality.",                     name: "Rishikesh Mhaske",  role: "Powertrain Head",          company: "Jaguar Land Rover UK" },
  { id: 9, quote: "Rarely do you find someone who understands both content and AI at this depth.",                                                                                                                          name: "Mayank Kumar",      role: "CEO",                      company: "Mosaic Wellness US" },
];

/* The rail used to ship the words "Swipe to explore all" in place of an
   affordance: it hides its scrollbar, has no edge fade and had no controls, so
   on a desktop pointer there was nothing to swipe and nothing to click. Two
   buttons replace the instruction. They step by one measured card plus the gap,
   so the rail always comes to rest on a card boundary rather than mid-quote. */
export const Testimonials = () => {
  const railRef = useRef(null);

  const step = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector("li");
    // 1rem is the flex gap; measuring the live card is what keeps the scroll
    // landing on an edge at all three breakpoints.
    const distance = card ? card.getBoundingClientRect().width + 16 : rail.clientWidth;
    rail.scrollBy({
      left: direction * distance,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return (
    <Section id="testimonials" data-reel-label="Testimonials">
      <SectionHeader
        eyebrow="Success Stories"
        title="Results, in"
        accent="their words."
        lede="Growth, production, and brand leads at Pocket FM, Pokus AI, Sugar.fit, MPL, Zostel, Mosaic Wellness, Philip Morris International, and Jaguar Land Rover, on the record."
      />

      <div className="mb-4 flex items-center justify-between gap-4">
        <span className="font-label text-label uppercase text-faint">
          {testimonials.length} voices
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Previous testimonials"
            className="flex h-11 w-11 items-center justify-center border border-line text-bone transition-colors duration-hover ease-gate hover:border-gold hover:text-gold"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Next testimonials"
            className="flex h-11 w-11 items-center justify-center border border-line text-bone transition-colors duration-hover ease-gate hover:border-gold hover:text-gold"
          >
            <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      </div>

      <ul
        ref={railRef}
        className="testimonial-rail flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {testimonials.map((t) => (
          <Panel
            as="li"
            key={t.id}
            className="testimonial-card flex min-h-72 min-w-0 shrink-0 snap-start flex-col justify-between overflow-hidden"
          >
            <blockquote className="min-w-0 max-w-full break-words font-body text-body text-bone">
              &ldquo;{t.quote}&rdquo;
            </blockquote>
            {/* This line is the proof. It used to be set at 10px in uppercase
                mono in the dimmest colour on the page, which is where the
                strongest names on the site — Philip Morris International,
                Jaguar Land Rover UK — were being hidden. */}
            <footer className="mt-7 border-t border-line pt-5">
              <p className="font-body text-body font-medium text-bone">{t.name}</p>
              <p className="mt-1 font-body text-body text-mute">
                {t.role}, {t.company}
              </p>
            </footer>
          </Panel>
        ))}
      </ul>
    </Section>
  );
};

export default Testimonials;
